<template>
  <div class="interface-docs-panel" v-loading="loading">
    <section class="docs-overview">
      <div class="overview-main">
        <div class="overview-kicker">MAZDA API</div>
        <h2>接口说明</h2>
        <p>维护 tool_MAZDA 功能号说明、启用版本和最新版本弃用状态。</p>
      </div>
      <div class="overview-stats">
        <div class="stat-box">
          <span>总数</span>
          <strong>{{ stats.total }}</strong>
        </div>
        <div class="stat-box">
          <span>新接口</span>
          <strong>{{ stats.current }}</strong>
        </div>
        <div class="stat-box">
          <span>旧接口</span>
          <strong>{{ stats.legacy }}</strong>
        </div>
        <div class="stat-box danger">
          <span>已弃用</span>
          <strong>{{ stats.deprecated }}</strong>
        </div>
      </div>
    </section>

    <el-card class="workspace-card docs-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <div>
            <span>功能号说明列表</span>
            <small>{{ redisDb || 'DB1 / mazda' }} · {{ redisKey || 'api:funcMazda:interfaceDocs' }}</small>
          </div>
          <div class="header-actions">
            <el-button type="primary" @click="openAddDialog">新增说明行</el-button>
            <el-button :loading="loading" @click="loadInterfaceDocs">刷新</el-button>
          </div>
        </div>
      </template>

      <div class="toolbar-grid">
        <el-input
          v-model="keyword"
          clearable
          placeholder="搜索功能号、说明、版本"
        />
        <el-select v-model="groupFilter" clearable placeholder="接口类型">
          <el-option
            v-for="item in groupOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <el-select v-model="statusFilter" clearable placeholder="状态">
          <el-option label="未弃用" value="active" />
          <el-option label="已弃用" value="deprecated" />
        </el-select>
      </div>

      <el-table
        :data="filteredRows"
        border
        stripe
        class="docs-table"
        empty-text="暂无接口说明"
      >
        <el-table-column label="功能号" width="92" fixed>
          <template #default="scope">
            <el-tag :type="scope.row.group === '旧接口' ? 'warning' : 'primary'">
              {{ scope.row.funcCode }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="功能说明" min-width="260">
          <template #default="scope">
            <el-input
              v-model="scope.row.description"
              type="textarea"
              :autosize="{ minRows: 1, maxRows: 3 }"
              maxlength="120"
              show-word-limit
            />
          </template>
        </el-table-column>

        <el-table-column label="版本启用" min-width="150">
          <template #default="scope">
            <el-input
              v-model="scope.row.enabledVersion"
              placeholder="默认 / V44.40"
              maxlength="6"
            />
          </template>
        </el-table-column>

        <el-table-column label="最新版本是否弃用" width="170" align="center">
          <template #default="scope">
            <el-switch
              :model-value="scope.row.latestDeprecated"
              active-text="是"
              inactive-text="否"
              @update:model-value="setDeprecated(scope.row, $event)"
            />
          </template>
        </el-table-column>

        <el-table-column label="弃用版本" min-width="150">
          <template #default="scope">
            <el-input
              v-model="scope.row.deprecatedVersion"
              :disabled="!scope.row.latestDeprecated"
              placeholder="V44.40"
              maxlength="6"
            />
          </template>
        </el-table-column>

        <el-table-column label="来源" min-width="230">
          <template #default="scope">
            <div class="source-cell">
              <el-tag effect="plain" :type="scope.row.group === '旧接口' ? 'warning' : 'success'">
                {{ scope.row.group }}
              </el-tag>
              <span>{{ scope.row.source }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="最近修改" min-width="190">
          <template #default="scope">
            <div class="update-cell">
              <strong>{{ scope.row.updatedBy || '-' }}</strong>
              <span>{{ scope.row.updatedAt || '-' }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="110" fixed="right" align="center">
          <template #default="scope">
            <el-button
              size="small"
              type="primary"
              :loading="savingFuncCode === scope.row.funcCode"
              @click="saveRow(scope.row)"
            >
              保存
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog v-model="addDialogVisible" title="新增接口说明行" width="640px" destroy-on-close>
      <el-form label-position="top" class="manual-form">
        <el-row :gutter="12">
          <el-col :xs="24" :md="8">
            <el-form-item label="功能号">
              <el-input v-model="manualForm.funcCode" placeholder="例如 234" maxlength="32" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="8">
            <el-form-item label="接口类型">
              <el-select
                v-model="manualForm.group"
                filterable
                allow-create
                default-first-option
                style="width: 100%;"
              >
                <el-option
                  v-for="item in groupOptions"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="8">
            <el-form-item label="版本启用">
              <el-input v-model="manualForm.enabledVersion" placeholder="默认 / V44.40" maxlength="6" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="功能说明">
          <el-input
            v-model="manualForm.description"
            type="textarea"
            :autosize="{ minRows: 2, maxRows: 4 }"
            maxlength="200"
            show-word-limit
            placeholder="手动输入这行接口说明"
          />
        </el-form-item>

        <el-row :gutter="12">
          <el-col :xs="24" :md="12">
            <el-form-item label="来源">
              <el-input v-model="manualForm.source" placeholder="例如 tool_MAZDA::AcquireCustom" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="6">
            <el-form-item label="最新版本是否弃用">
              <el-switch
                :model-value="manualForm.latestDeprecated"
                active-text="是"
                inactive-text="否"
                @update:model-value="setDeprecated(manualForm, $event)"
              />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="6">
            <el-form-item label="弃用版本">
              <el-input
                v-model="manualForm.deprecatedVersion"
                :disabled="!manualForm.latestDeprecated"
                placeholder="V44.40"
                maxlength="6"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="addDialogVisible = false">取消</el-button>
          <el-button
            type="primary"
            :loading="savingFuncCode === manualForm.funcCode"
            @click="submitManualRow"
          >
            保存
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { toRef } from 'vue';
import { useMazdaInterfaceDocs } from '@/composables/mazda/useMazdaInterfaceDocs';

const props = defineProps({
  mazdaPost: { type: Function, required: true },
  serverName: { type: String, required: true },
});

const {
  loading,
  savingFuncCode,
  filteredRows,
  redisKey,
  redisDb,
  keyword,
  groupFilter,
  statusFilter,
  addDialogVisible,
  manualForm,
  stats,
  groupOptions,
  loadInterfaceDocs,
  saveRow,
  setDeprecated,
  openAddDialog,
  submitManualRow,
} = useMazdaInterfaceDocs(props.mazdaPost, toRef(props, 'serverName'));
</script>

<style scoped>
.interface-docs-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.docs-overview {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) auto;
  gap: 14px;
  align-items: stretch;
  padding: 16px;
  border: 1px solid rgba(224, 231, 241, 0.98);
  border-radius: 14px;
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(246, 250, 255, 0.94)),
    linear-gradient(135deg, rgba(64, 158, 255, 0.1), rgba(103, 194, 58, 0.08));
  box-shadow: 0 8px 18px rgba(24, 40, 72, 0.06);
}

.overview-main {
  min-width: 0;
}

.overview-kicker {
  font-size: 12px;
  font-weight: 800;
  color: #3274d9;
  letter-spacing: 0;
}

.overview-main h2 {
  margin: 6px 0 0;
  font-size: 22px;
  line-height: 1.2;
  color: #1f2d3d;
}

.overview-main p {
  margin: 8px 0 0;
  color: #667085;
  font-size: 13px;
  line-height: 1.55;
}

.overview-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(92px, 1fr));
  gap: 10px;
  min-width: 420px;
}

.stat-box {
  min-height: 72px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #e6edf7;
  background: rgba(255, 255, 255, 0.86);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
}

.stat-box span {
  color: #7c8798;
  font-size: 12px;
}

.stat-box strong {
  color: #1f2d3d;
  font-size: 22px;
  line-height: 1;
}

.stat-box.danger strong {
  color: #d94b4b;
}

.docs-card {
  border-radius: 8px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.card-header > div:first-child {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.card-header span {
  color: #1f2d3d;
  font-weight: 800;
}

.card-header small {
  color: #8a94a6;
  font-size: 12px;
  word-break: break-all;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.toolbar-grid {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) minmax(150px, 180px) minmax(140px, 160px);
  gap: 10px;
  margin-bottom: 12px;
}

.docs-table {
  width: 100%;
}

.source-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.source-cell span:last-child {
  min-width: 0;
  color: #606266;
  font-size: 12px;
  word-break: break-all;
}

.update-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #7c8798;
  font-size: 12px;
}

.update-cell strong {
  color: #3f4a5a;
  font-size: 12px;
}

.manual-form {
  min-width: 0;
}

.dialog-footer {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

@media (max-width: 1180px) {
  .docs-overview {
    grid-template-columns: 1fr;
  }

  .overview-stats {
    min-width: 0;
  }
}

@media (max-width: 760px) {
  .overview-stats,
  .toolbar-grid {
    grid-template-columns: 1fr;
  }

  .overview-main h2 {
    font-size: 20px;
  }
}
</style>
